import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, Toolbar, useMediaQuery, useTheme } from '@mui/material';
import Header from './Header';
import Sidebar from './Sidebar';
import GlobalActivityBar from './GlobalActivityBar';
import PageSkeleton from './PageSkeleton';
import { apiActivity } from '../../services/apiActivity';
import { useAppTheme } from '../../theme';
import { useAuth } from '../../context/AuthContext';
import { useSessionHeartbeat } from '../../hooks/useSessionHeartbeat';
import { RAIL_WIDTH, RAIL_EXPANDED_WIDTH } from '../shell/shellTokens';
import { useSidebarPin } from '../shell/useSidebarPin';
import { useRailState } from '../shell/useRailState';

const AppLayout: React.FC = () => {
  const theme = useTheme();
  const { colors } = useAppTheme();
  const { isAuthenticated } = useAuth();
  useSessionHeartbeat(isAuthenticated);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [pinned, togglePin] = useSidebarPin();
  const { expanded: railExpanded, railProps } = useRailState(pinned);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setDesktopOpen(!desktopOpen);
    }
  };

  const isOpen = isMobile ? mobileOpen : desktopOpen;

  /**
   * The width the LAYOUT reserves for navigation.
   *
   * Tracks the rail's CURRENT width, expanded or not, so opening the rail
   * pushes the workspace across rather than floating over it — the page is
   * never hidden under the menu. The nav column, the AppBar and the drawer
   * paper all animate off this one number on the same curve, so the three
   * move as a single edge instead of sliding apart.
   *
   * On mobile the rail is a modal overlay and reserves nothing.
   */
  const navWidth = isMobile || !isOpen ? 0 : railExpanded ? RAIL_EXPANDED_WIDTH : RAIL_WIDTH;

  // Route-transition skeleton: on every navigation, show a skeleton in the
  // content pane until the new page's initial API calls settle (min 300ms so it
  // doesn't flash, max 1500ms so a lingering background poll never traps it).
  const { pathname } = useLocation();
  const [routeLoading, setRouteLoading] = useState(false);
  useEffect(() => {
    setRouteLoading(true);
    const start = Date.now();
    const MIN = 300;
    const MAX = 1500;
    let done = false;
    const finish = () => {
      if (!done) {
        done = true;
        setRouteLoading(false);
      }
    };
    const unsub = apiActivity.subscribe((active) => {
      if (!active && Date.now() - start >= MIN) finish();
    });
    const minTimer = setTimeout(() => {
      if (apiActivity.count === 0) finish();
    }, MIN);
    const maxTimer = setTimeout(finish, MAX);
    return () => {
      unsub();
      clearTimeout(minTimer);
      clearTimeout(maxTimer);
    };
  }, [pathname]);

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', width: '100%' }}>
      <GlobalActivityBar />
      <Header
        open={isOpen}
        handleDrawerToggle={handleDrawerToggle}
        navWidth={navWidth}
      />
      <Sidebar
        open={isOpen}
        handleDrawerToggle={handleDrawerToggle}
        pinned={pinned}
        onTogglePin={togglePin}
        expanded={railExpanded}
        railProps={railProps}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          minWidth: 0,
          position: 'relative',
          backgroundColor: colors.background,
          height: '100vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        {/* The page mounts underneath and fires its requests; the skeleton
            overlays it until that first load settles (see the effect above). */}
        <Outlet />
        {routeLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundColor: colors.background,
              overflowY: 'auto',
              overflowX: 'hidden',
              px: 3,
              pb: 3,
              zIndex: 3,
            }}
          >
            <Toolbar />
            <PageSkeleton />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AppLayout;